import type { HeroRole } from "$lib/types";

/** A question/answer pair rendered as visible content and FAQPage structured data. */
export interface Faq {
    question: string;
    answer: string;
}

const ROLE_NOUN: Record<HeroRole, string> = {
    tank: "tank",
    damage: "DPS",
    support: "support",
};

/** Format a 0–1 rate as a one-decimal percent for natural prose, e.g. 0.512 → "51.2%". */
function pct(value: number): string {
    return `${(value * 100).toFixed(1)}%`;
}

/** Ordinal for small rank numbers, e.g. 1 → "1st", 3 → "3rd". */
function ordinal(n: number): string {
    const tens = n % 100;
    if (tens >= 11 && tens <= 13) return `${n}th`;
    switch (n % 10) {
        case 1:
            return `${n}st`;
        case 2:
            return `${n}nd`;
        case 3:
            return `${n}rd`;
        default:
            return `${n}th`;
    }
}

/** Oxford-comma join, e.g. ["a","b","c"] → "a, b, and c". */
function listJoin(items: string[]): string {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

/** Indefinite article for a label based on its first letter. */
function article(word: string): string {
    return /^[aeiou]/i.test(word) ? "an" : "a";
}

export interface HeroProseInput {
    name: string;
    role: HeroRole;
    tier: string;
    winRate: number;
    pickRate: number;
    banRate: number | null;
    /** 1-based rank by win rate among heroes of the same role. */
    rank: number;
    roleCount: number;
    /** Highest-win-rate map for this hero, if known. */
    bestMapName?: string;
    bestMapWinRate?: number;
}

/** Unique, data-driven intro paragraph for a hero detail page. */
export function heroIntro(h: HeroProseInput): string {
    const roleNoun = ROLE_NOUN[h.role];
    const parts: string[] = [
        `${h.name} is ${article(h.tier)} ${h.tier}-tier ${roleNoun} in the current Overwatch meta, with a ${pct(h.winRate)} win rate and a ${pct(h.pickRate)} pick rate in competitive play.`,
    ];
    if (h.rank > 0 && h.roleCount > 0) {
        parts.push(`That ranks ${h.name} ${ordinal(h.rank)} of ${h.roleCount} ${roleNoun} heroes by win rate.`);
    }
    if (h.bestMapName) {
        const wr = h.bestMapWinRate !== undefined ? ` (${pct(h.bestMapWinRate)} win rate)` : "";
        parts.push(`${h.name} performs best on ${h.bestMapName}${wr}.`);
    }
    parts.push(`All ${h.name} Overwatch stats below come from official Blizzard data and update daily.`);
    return parts.join(" ");
}

/** Data-driven FAQ entries for a hero detail page. */
export function heroFaqs(h: HeroProseInput): Faq[] {
    const roleNoun = ROLE_NOUN[h.role];
    const isStrong = h.tier === "S" || h.tier === "A";
    const rankClause =
        h.rank > 0 ? ` ${h.name} ranks ${ordinal(h.rank)} of ${h.roleCount} ${roleNoun} heroes by win rate.` : "";
    const banClause = h.banRate !== null && h.banRate >= 0 ? `, and is banned in ${pct(h.banRate)} of matches` : "";

    const faqs: Faq[] = [
        {
            question: `Is ${h.name} good in Overwatch right now?`,
            answer: `${h.name} is currently ${h.tier} tier${isStrong ? ", making it one of the stronger picks" : ""}, with a ${pct(h.winRate)} win rate in competitive play.${rankClause}`,
        },
        {
            question: `What is ${h.name}'s win rate in Overwatch?`,
            answer: `${h.name} has a ${pct(h.winRate)} win rate and a ${pct(h.pickRate)} pick rate in competitive Overwatch${banClause}. These figures come from official Blizzard data and update daily.`,
        },
        {
            question: `What tier is ${h.name}?`,
            answer: `${h.name} is ${h.tier} tier on the OverwatchBrain hero tier list, ranked from win rate, pick rate, and ban rate across all skill ranks.`,
        },
    ];

    if (h.bestMapName) {
        const wr = h.bestMapWinRate !== undefined ? ` (${pct(h.bestMapWinRate)})` : "";
        faqs.push({
            question: `What is the best map for ${h.name}?`,
            answer: `${h.name} has its highest win rate on ${h.bestMapName}${wr}. See the map performance table above for ${h.name}'s win rate on every Overwatch map.`,
        });
    }
    return faqs;
}

export interface MapHeroPick {
    name: string;
    winRate: number;
}

export interface MapProseInput {
    mapName: string;
    /** Display label for the game mode, e.g. "Hybrid", "Escort". */
    mapTypeLabel?: string;
    /** Top heroes overall on this map, ordered by win rate. */
    topHeroes: string[];
    bestTank?: MapHeroPick;
    bestDps?: MapHeroPick;
    bestSupport?: MapHeroPick;
}

/** Unique, data-driven intro paragraph for a map detail page. */
export function mapIntro(m: MapProseInput): string {
    const parts: string[] = [];
    parts.push(
        m.mapTypeLabel
            ? `${m.mapName} is ${article(m.mapTypeLabel)} ${m.mapTypeLabel} map in Overwatch.`
            : `${m.mapName} is an Overwatch map.`,
    );
    if (m.topHeroes.length) {
        parts.push(`The best heroes on ${m.mapName} right now are ${listJoin(m.topHeroes.slice(0, 3))} by win rate.`);
    }
    parts.push(`These ${m.mapName} hero win rates come from official Blizzard data and update daily.`);
    return parts.join(" ");
}

/** Data-driven FAQ entries for a map detail page. */
export function mapFaqs(m: MapProseInput): Faq[] {
    const faqs: Faq[] = [];

    if (m.topHeroes.length) {
        faqs.push({
            question: `Who are the best heroes on ${m.mapName}?`,
            answer: `The strongest heroes on ${m.mapName} by win rate are ${listJoin(m.topHeroes.slice(0, 5))}, based on official Blizzard data updated daily.`,
        });
    }

    const roleLines: string[] = [];
    if (m.bestTank) roleLines.push(`the best tank is ${m.bestTank.name} (${pct(m.bestTank.winRate)} win rate)`);
    if (m.bestDps) roleLines.push(`the best DPS is ${m.bestDps.name} (${pct(m.bestDps.winRate)})`);
    if (m.bestSupport) roleLines.push(`the best support is ${m.bestSupport.name} (${pct(m.bestSupport.winRate)})`);
    if (roleLines.length) {
        faqs.push({
            question: `What is the best hero for each role on ${m.mapName}?`,
            answer: `On ${m.mapName}, ${listJoin(roleLines)}.`,
        });
    }

    if (m.mapTypeLabel) {
        faqs.push({
            question: `What type of map is ${m.mapName}?`,
            answer: `${m.mapName} is ${article(m.mapTypeLabel)} ${m.mapTypeLabel} map in Overwatch.`,
        });
    }

    faqs.push({
        question: `How often are ${m.mapName} stats updated?`,
        answer: `${m.mapName} hero win rates, pick rates, and ban rates are sourced from official Blizzard data and refreshed daily.`,
    });

    return faqs;
}
